<script setup lang="ts">
import type { Money } from '@taler/domain'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { plainAmount, useAccountingView } from '@/app/accounting-view'
import { useI18n } from 'vue-i18n'
import type { RollupTreeRow } from '../internal/rollup-tree.ts'

const props = defineProps<{ rows: readonly RollupTreeRow[]; accountClass: string }>()
const { t } = useI18n()
const { accountingView } = useAccountingView()

function shown(value: Money): Money {
  return plainAmount(value, props.accountClass, accountingView.value)
}
</script>

<template>
  <div
    tabindex="0"
    role="region"
    :aria-label="t('reporting.rollupTitle')"
    class="overflow-x-auto rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
  >
    <table class="w-full border-collapse">
      <thead>
        <tr class="h-[var(--density-row)] border-b border-[color:var(--color-border)]">
          <th
            scope="col"
            class="px-[var(--space-cell)] text-left text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            {{ t('reporting.rollupAccountColumn') }}
          </th>
          <th
            scope="col"
            class="px-[var(--space-cell)] text-right text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            {{ t('reporting.rollupOwnColumn') }}
          </th>
          <th
            scope="col"
            class="px-[var(--space-cell)] text-right text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            {{ t('reporting.rollupRolledUpColumn') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="row in rows" :key="row.id">
          <!-- A real account row: postable-tree affordances (indentation, chevron space) apply. -->
          <tr
            v-if="row.kind === 'account'"
            data-row-kind="account"
            class="h-[var(--density-row)] border-b border-[color:var(--color-border)] last:border-0 hover:bg-[color:var(--color-surface-raised)]"
          >
            <th
              scope="row"
              class="px-[var(--space-cell)] text-left text-[length:var(--text-step-2)] font-normal text-[color:var(--color-text)]"
              :style="{ paddingLeft: `calc(var(--space-block) * ${row.depth} + var(--space-cell))` }"
            >
              <span
                aria-hidden="true"
                class="mr-[var(--space-inline)] inline-block w-3 text-[color:var(--color-text-muted)]"
                >{{ row.hasChildren ? '▸' : '' }}</span
              >
              {{ row.label }}
            </th>
            <td class="px-[var(--space-cell)]">
              <MoneyAmount :value="shown(row.ownBalance)" />
            </td>
            <td class="px-[var(--space-cell)]">
              <MoneyAmount :value="shown(row.rolledUpBalance)" />
            </td>
          </tr>

          <!--
            The "Egyéb" (Other) row: not an account, so no chevron, no row-level menu, and no
            interactive/treeitem role. The attention token marks it as a figure the user did
            not enter (packages/ui/src/tokens.css).
          -->
          <tr
            v-else
            data-row-kind="other"
            class="h-[var(--density-row)] bg-[color:color-mix(in_srgb,var(--color-attention)_10%,transparent)]"
          >
            <td
              class="px-[var(--space-cell)] text-left text-[length:var(--text-step-2)] italic text-[color:var(--color-attention)]"
              :style="{ paddingLeft: `calc(var(--space-block) * ${row.depth} + var(--space-cell) + 1rem)` }"
            >
              {{ t('reporting.rollupOtherRow') }}
            </td>
            <td class="px-[var(--space-cell)]">
              <MoneyAmount :value="shown(row.amount)" />
            </td>
            <td class="px-[var(--space-cell)]" />
          </tr>
        </template>
        <tr v-if="rows.length === 0">
          <td colspan="3" class="px-[var(--space-cell)] py-[var(--space-block)]">
            <p class="text-[color:var(--color-text-muted)]">{{ t('reporting.rollupEmpty') }}</p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
